import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'

import type { IncidenceQueryData } from 'modules/bookkeepers/hooks/useGetIncidences'
import useCurrentIncidenceCompany from 'modules/bookkeepers/hooks/useIncidenceCompany'
import taxonomies, { supplementTaxonomyName } from 'modules/contracts/store/collections/taxonomies'
import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import { centsToString } from 'modules/payroll_bundle/lib/utils'
import { PolicyPeriods } from 'modules/payroll_bundle/store/collections/policyPeriods'
import { SupplementObservations } from 'modules/payroll_bundle/store/collections/supplementObservations'
import { Supplements } from 'modules/payroll_bundle/store/collections/supplements'
import PolicyPeriod from 'modules/payroll_bundle/store/models/PolicyPeriod'

interface SupplementLineProps {
  name: string
  value: string | null
}
const AdditionalSupplementLine = ({ name, value }: SupplementLineProps) => {
  return (
    <Box flexDirection="row" gap="s8">
      <Box minWidth="s96">
        <Text.Normal weight="semibold" size="200">
          {name}
        </Text.Normal>
      </Box>
      <Text.Normal size="200">{value}</Text.Normal>
    </Box>
  )
}

interface Props {
  incidence: IncidenceQueryData
  isBookkeeperView: boolean
}
const AdditionalSupplement = ({ incidence, isBookkeeperView }: Props) => {
  const policyPeriodCollection = React.useRef(new PolicyPeriods()).current
  const supplementsCollection = React.useRef(new Supplements()).current
  const supplementObservationsCollection = React.useRef(new SupplementObservations()).current
  const incidenceId = incidence.id
  // In terminations, the target_id is the employee_id
  const employeeId = incidence.name === INCIDENCE_TYPES.termination ? incidence.targetId : null
  const company = useCurrentIncidenceCompany(incidence)

  const loadSupplements = async () => {
    if (!employeeId) return

    await policyPeriodCollection.fetch({
      data: {
        by_current_periods: incidence.startsOn,
        bookkeeper_view: isBookkeeperView,
      },
    })

    const policyPeriodIds = policyPeriodCollection.map((policy) => policy.get('id'))
    if (!policyPeriodIds.length) return

    await supplementsCollection.fetch({
      data: {
        employee_ids: [employeeId],
        policy_period_ids: policyPeriodIds,
        bookkeeper_view: isBookkeeperView,
      },
    })

    if (!supplementsCollection.length) return

    await supplementObservationsCollection.fetch({
      data: {
        supplement_ids: supplementsCollection.map((s) => s.id),
        bookkeeper_view: isBookkeeperView,
      },
    })
  }

  const { loading } = useAsyncData(loadSupplements, [incidenceId])

  const { loading: loadingTaxonomies } = useAsyncData(
    () => taxonomies.fetch({ data: { bookkeeper_view: isBookkeeperView } }),
    [isBookkeeperView]
  )

  const findObservation = (supplementId: number) => {
    return supplementObservationsCollection.find({ supplement_id: supplementId })?.attributes.text
  }

  const findPeriod = (periodId: number) => {
    return policyPeriodCollection.find({ id: periodId })
  }

  const findPeriodName = (periodId: number) => {
    return findPeriod(periodId)?.attributes.name
  }

  const findPeriodStatus = (periodId: number) => {
    const period = findPeriod(periodId)
    if (!period) return ''

    const statusTranslation = PolicyPeriod.Statuses()
    const periodStatus = period.attributes.status as keyof typeof statusTranslation
    return statusTranslation[periodStatus]
  }

  if (loading || loadingTaxonomies) return <Loading />
  if (supplementsCollection.models.length == 0) return null
  return (
    <>
      <Box gap="s8">
        <Text.Normal size="200">
          {i18n.t('bookkeepers.incidence.details.additional_supplements')}
        </Text.Normal>
        <Box gap="s16">
          {supplementsCollection.models.map((s) => (
            <Box
              key={s.id}
              gap="s8"
              background="grey200"
              borderRadius={{
                all: 'abs008',
              }}
              paddingX="s24"
              paddingY="s16"
            >
              <Text.Normal weight="medium" color="grey1000">
                {supplementTaxonomyName(s)}
              </Text.Normal>
              <AdditionalSupplementLine
                name={i18n.t('bookkeepers.additional_supplement.labels.amount')}
                value={centsToString(s.attributes.amount_in_cents, s.attributes.unit, company)}
              />
              <AdditionalSupplementLine
                name={i18n.t('bookkeepers.additional_supplement.labels.period')}
                value={findPeriodName(s.attributes.payroll_policy_period_id)}
              />
              <AdditionalSupplementLine
                name={i18n.t('bookkeepers.additional_supplement.labels.status')}
                value={findPeriodStatus(s.attributes.payroll_policy_period_id)}
              />
              <AdditionalSupplementLine
                name={i18n.t('bookkeepers.additional_supplement.labels.observation')}
                value={findObservation(s.id)}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </>
  )
}
export default observer(AdditionalSupplement)
